<template>
    <div class="receipt-invoice-me invoice">

        <!-- Invoice Header -->
        <div
            v-if="tenant.venue.invoice_header"
            class="ck-content ck-custom"
            v-html="tenant.venue.invoice_header"
        />

        <!-- Header -->
        <div
            v-else
            class="receipt-header"
        >
            <p>{{ tenant.venue.company_name }}</p>
            <p>{{ tenant.venue.name }}</p>
            <p v-if="tenant.venue.tax_id_number && tenant.venue.tax_id_number !== 13">
                {{ $t('general.tax_id_number') + ': ' + tenant.venue.tax_id_number }}
            </p>
            <p>{{ tenant.venue.address }}</p>
        </div>

        <div class="receipt-subheader">

            <!-- Receipt Details -->
            <div class="receipt-details">
                <p v-if="order.internal_number">
                    {{ `${$t('general.credit_note')}: ${order.internal_number}` }}
                </p>
                <p v-if="order.number && locale !== 'me'">{{ $t('general.receipt_number_serial') + ': ' + order.number }}</p>
                <p v-if="order.number && locale === 'me'">{{ $t('general.receipt_number') + ': ' + order.getFullNumber() }}</p>
                <p v-if="order.getUser()">{{ $t('general.operator') + ': ' + order.getUser().name + ' / ' + order.getUser().hash }}</p>
                <p v-if="order.getLastActivityAt()">{{ $t('general.time') + ': ' }} {{ order.getLastActivityAt() | dateTime }}</p>
                <p v-if="order.isSummary()">{{ $t('general.receipt_type') + ': ' + $t('general.receipt_summary') }}</p>
                <p v-if="order.paymentType()">{{ $t('general.paid_by') + ': ' + order.paymentType() }}</p>
                <p v-if="order.due_date">{{ $t('general.due_date') }}: {{ order.due_date | date }}</p>
            </div>

            <!-- Invoice Client Details -->
            <div v-if="client" class="receipt-buyer">
                <p><strong>{{ $tc('general.buyer', 1) }}</strong></p>
                <p v-if="client.name">{{ client.name }}</p>
                <p v-if="client.cost_center">
                    {{ $t('general.buyer_cost_center') + ': ' + client.cost_center }}
                </p>
                <p v-if="client.address">
                    {{ `${client.address}` }}
                    <template v-if="client.city">, {{ `${client.city}` }}</template>
                    <template v-if="client.country">, {{ `${client.country}` }}</template>
                </p>
                <p v-if="(client.country && client.country !== 'MNE') && client.id_number">
                    {{ $t(`clients.id_types.${client.id_type}`) + ': ' + client.id_number }}
                </p>
                <p v-if="client.registration_number">
                    {{ $t('general.registration_number') + ': ' + client.registration_number }}
                </p>
                <p v-if="client.tax_id_number && client.tax_id_number !== client.id_number">
                    {{ $t('general.tax_id_number') + ': ' + client.tax_id_number }}
                </p>
                <p v-if="client.tax_id_card_number">
                    {{ $t('general.tax_id_card_number') + ': ' + client.tax_id_card_number }}
                </p>
                <p v-if="client.vat_number">
                    {{ $t('general.vat_number_short') + ': ' + client.vat_number }}
                </p>
            </div>

        </div>

        <div v-if="order.start_date && order.end_date" class="receipt-period">
            {{ $t('invoice.period') }}: {{ order.start_date | date }} - {{ order.end_date | date }}
        </div>

        <!-- Receipt Items -->
        <table class="receipt-items">
            <tbody>

                <!-- Heading Row -->
                <tr v-if="productItems.length" class="heading-row">
                    <template v-if="order.flag !== 'summaryCreditNote'">
                        <td align="left">{{ $t('general.number_short') }}</td>
                        <td align="left">{{ $t('general.code') }}</td>
                        <td align="left" class="name">{{ $tc('general.item', 1) }}</td>
                        <td align="right">{{ $t('invoice_preview.quantity') }}</td>
                        <td align="right" width="70px">{{ $t('invoice_preview.unit_price_before_tax') }}</td>
                        <td align="right">{{ $t('invoice_preview.tax_rate') }}</td>
                        <td align="right" width="70px">{{ $t('invoice_preview.unit_price') }}</td>
                        <td align="right">{{ $t('invoice_preview.discount') }}</td>
                        <td align="right" width="90px">{{ $t('invoice_preview.unit_price_after_discount') }}</td>
                        <td align="right" width="90px">{{ $t('invoice_preview.price_before_tax') }}</td>

                        <td align="right" width="90px">{{ $t('general.return_count') }}</td>
                        <td align="right" width="90px">{{ $t('general.additional_discount') }}</td>
                        <td align="right" width="90px">{{ $t('general.credit_note_amount') }}</td>
                    </template>

                    <template v-else>
                        <td align="left" class="name">{{ $t('tax_rates.rate') }}</td>
                        <td align="right">{{ $t('general.unit_price_before_tax') }}</td>
                        <td align="right">{{ $t('general.tax_rate') }}</td>
                        <td align="right">{{ $t('general.tax_amount') }}</td>
                        <td align="right">{{ $t('general.additional_discount') }}</td>
                        <td align="right">{{ $t('general.credit_note_amount') }}</td>
                    </template>
                </tr>

                <!-- Order Items -->
                <tr
                    v-for="(item, index) in productItems"
                    :key="`invoice-${index}`"
                >
                    <template v-if="order.flag !== 'summaryCreditNote'">
                        <td align="left">{{ index + 1 }}.</td>
                        <td align="left">
                            {{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}
                        </td>
                        <td align="left" class="name">
                            <div style="white-space: normal; word-break: break-word;">
                                {{ item.name }}
                                <template v-if="isAllegra && item.comments.length">
                                    <br>
                                    <span style="font-size: 11px;" v-html="nl2br(item.comments[0])"></span>
                                </template>
                            </div>
                        </td>

                        <td align="right">
                            {{ item.getCount() }}
                            <small>{{ item.getProduct() ? item.getProduct().getUnitOfMeasureSold() : '' }}</small>
                        </td>
                        <td align="right">{{ item.getUnitPriceBeforeTax().toFormat('0.0000') }}</td>
                        <td align="right">{{ item.getTaxRate() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getDiscountPercent() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterDiscount().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getPriceBeforeTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getAdditionalCount() }}</td>
                        <td align="right">{{ item.getAdditionalDiscountPercent() }}%</td>
                        <td align="right">{{ item.getAdditionalPriceBeforeTax().toFormat('0.00') }}</td>
                    </template>

                    <template v-else>
                        <td align="left" class="name">{{ item.name }}</td>
                        <td align="right">{{ item.getUnitPriceBeforeTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getTaxRate() }}%</td>
                        <td align="right">{{ item.getTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getAdditionalDiscountPercent() }}%</td>
                        <td align="right">{{ item.getAdditionalPriceBeforeTax().toFormat('0.00') }}</td>
                    </template>
                </tr>

                <tr class="divider">
                    <td :colspan="13" />
                </tr>
            </tbody>
        </table>

        <div class="receipt-footer">
            <div class="footer-item additional-info">
                <p v-if="locale === 'me'">
                    <span v-if="order.correctsOrders().length" class="code">
                        REF IKOF: {{ order.correctsOrders()[0].getIic() }}
                    </span>
                    <span class="code">
                        IKOF: {{ order.getIic() }}
                    </span>
                    <span v-if="order.fiscal_code" class="code">
                        JIKR: {{ order.fiscal_code }}
                    </span>
                </p>

                <p v-if="order.note" class="note">
                    {{ order.note }}
                </p>

                <p v-if="!tenant.venue.taxes_enabled" class="note">
                    {{ $t('general.note') }}: {{ $t('order.exempt_from_tax_comment') }}
                </p>

                <div class="signatures">
                    <div class="signature">
                        <div>___________________</div>
                        <div>{{ $tc('invoice.delivered_by') }}</div>
                    </div>

                    <div class="signature">
                        <div>___________________</div>
                        <div>{{ $tc('invoice.received_by') }}</div>
                    </div>
                </div>
            </div>

            <div v-if="locale === 'me'" class="footer-item qrcode">
                <qrcode
                    :value="order.generateQRCodeString()"
                    :options="{
                        width: 150,
                        errorCorrectionLevel: 'L',
                        margin: 0,
                        scale: 6,
                    }"
                    tag="img"
                />
            </div>

            <div class="footer-item summary">
                <template v-if="tenant.venue.taxes_enabled">
                    <template v-for="(tax, index) in taxes">
                        <div
                            v-if="!tax.id"
                            :key="index"
                            class="summary-item"
                        >
                            <p>{{ $t('order.exempt_from_tax') }} {{ tax.label.replace('VAT_','') }}:</p>
                            <p class="amount">{{ Money.parse(tax.price_before_tax).toFormat('0.00') }}</p>
                        </div>
                    </template>

                    <template v-for="(tax, index) in taxes">
                        <div
                            v-if="tax.id"
                            :key="'beforetax-' + index"
                            class="summary-item"
                        >
                            <p>{{ `${tax.label} ${$t('general.price_pre_tax').toLowerCase()}:` }}</p>
                            <p class="amount">{{ Money.parse(tax.price_before_tax).toFormat('0.00') }}</p>
                        </div>

                        <div
                            v-if="tax.id"
                            :key="index"
                            class="summary-item"
                        >
                            <p>{{ `${tax.label} (${tax.rate}%):` }}</p>
                            <p class="amount">{{ Money.parse(tax.value).toFormat('0.00') }}</p>
                        </div>
                    </template>

                    <div v-if="taxes.length" class="divider" style="margin: 5px 0"></div>

                    <div v-if="summary.exemptFromTax && summary.exemptFromTax.toUnit() > 0" class="summary-item">
                        <p>{{ $t('invoice_preview.summary.exempt_from_tax') }}:</p>
                        <p class="amount">{{ summary.exemptFromTax.toFormat('0.00') }}</p>
                    </div>

                    <div v-if="summary.priceBeforeTax" class="summary-item">
                        <p>{{ $t('invoice_preview.summary.price_before_tax') }}:</p>
                        <p class="amount">{{ summary.priceBeforeTax.toFormat('0.00') }}</p>
                    </div>

                    <div class="summary-item">
                        <p>{{ $t('invoice_preview.summary.tax') }}:</p>
                        <p class="amount">{{ order.getTax().toFormat('0.00') }}</p>
                    </div>

                    <div class="divider" style="margin: 5px 0"></div>
                </template>

                <div class="summary-item">
                    <p>{{ $t('invoice_preview.summary.discount') }}:</p>
                    <p class="amount">{{ summary.additionalDiscountAmount.toFormat('0.00') }}</p>
                </div>

                <div class="summary-item">
                    <p>{{ $t('general.return_amount') }}:</p>
                    <p class="amount">{{ summary.additionalCountAmount.toFormat('0.00') }}</p>
                </div>

                <div class="divider" style="margin: 5px 0"></div>

                <div v-if="summary.toPay" class="summary-item to-pay">
                    <p>{{ $t('general.total') }}:</p>
                    <p class="amount">{{ summary.toPay.toFormat('0.00') }}</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import Client from '@/models/Client';

export default {
    props: {
        order: {
            type: Object,
            default: () => null
        },
        options: {
            type: Object,
            default: () => {}
        },
    },

    computed: {
        client() {
            return this.order.client || Client.find(this.order.client_id)
        },

        productItems() {
            if(this.order.isSummary()) {
                return this.order.paysForOrders() ? this.order.paysForOrders().flatMap(order => order.items) : []
            }

            return this.order.items
        },

        taxes() {
            return this.order.taxes.filter(taxItem => !!taxItem.id || (!taxItem.id && taxItem.label.includes('VAT_')))
        },

        summary() {
            if(this.order.isSummary() && !this.order.paysForOrders()) {
                return { toPay: this.order.getToPay() }
            }

            return {
                exemptFromTax: this.order.getExemptFromTax(),
                priceBeforeTax: this.order.getPriceBeforeTax(),
                additionalCountAmount: this.order.getAdditionalCountAmount(),
                additionalDiscountAmount: this.order.getAdditionalDiscountAmount(),
                discountAmount: this.order.getDiscountAmount().multiply(-1),
                toPay: this.order.getToPay(),
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.receipt-invoice-me {
    &.invoice {
        font-size: 13px;
        color: black;
        -webkit-user-select: text;
        -webkit-app-region: none;

        p {
            padding: 2px 0 !important;
            margin: 0 !important;
        }

        .divider {
            border-top: 1px solid black;
            height: 1px !important;
        }

        .receipt-header {
            padding: 10px;
            text-align: center;
            font-size: 16px;
            font-weight: 500;
        }

        .receipt-subheader {
            width: 100%;
            padding: 10px;
            display: flex;
            flex-wrap: wrap-reverse;
            align-items: center;

            .receipt-details {
                flex: 1;
                min-width: 300px;
            }

            .receipt-buyer {
                border-radius: 4px;
                border: 1px solid black;
                width: 250px;
                padding: 10px;
                margin: 10px 10px 10px 0;
            }
        }

        .receipt-period {
            padding: 10px;
        }

        .receipt-items {
            border-collapse: collapse;
            width: 100%;

            tbody {

                tr {
                    height: 35px;
                    vertical-align: top;

                    td {
                        padding: 5px;

                        &:first-child {
                            padding-left: 10px;
                        }

                        &.name {
                            min-width: 130px;
                            max-width: 200px;
                            word-break: break-word;
                        }

                        &:last-child {
                            padding-right: 10px;
                        }
                    }

                    &:hover {
                        background: #f7f7f7;
                    }
                }

                .heading-row {
                    padding: 0 5px;
                    vertical-align: middle;
                    border-top: 1px solid black;
                    border-bottom: 1px solid black;
                    height: 40px;
                    font-size: 11px;
                    font-weight: 600;
                    background: #f1f1f1;
                }
            }
        }

        .receipt-footer {
            display: flex;
            flex-wrap: wrap-reverse;
            margin-bottom: 20px;
            break-inside: avoid;

            .footer-item {
                flex: 1;

                &.additional-info {
                    min-width: 300px;
                    padding: 10px 0 10px 10px;

                    .code {
                        display: block;
                    }

                    .note {
                        margin-top: 10px !important;
                        white-space: pre-line;
                        font-size: 12px;
                        max-width: 400px;
                        text-align: justify;
                    }

                    .signatures {
                        display: flex;
                        flex-wrap: wrap;

                        .signature {
                            margin-top: 20px;
                            text-align: center;

                            &:first-child {
                                margin-right: 20px;
                            }
                        }
                    }
                }

                &.qrcode {
                    max-width: 170px;
                    text-align: center;
                    padding: 10px;
                }

                &.summary {
                    padding: 10px 10px 10px 0;
                    min-width: 220px;
                    max-width: 250px;

                    .summary-item {
                        padding: 1px;
                        display: flex;
                        justify-content: space-between;

                        &.to-pay {
                            font-weight: 600;
                            font-size: 16px;
                        }

                        .amount {
                            text-align: right;
                        }
                    }
                }
            }
        }

    }
}
</style>
